import { AnimatePresence, motion } from "framer-motion";
import { Loader2, Search, X } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import useDebouncedValue from "../../../../hooks/useDebouncedValue";
import { usePermissions } from "../../../../hooks/usePermissions";
import { Company } from "../../../../features/company/@types";
import { useGetCompaniesMutation } from "../../../../features/company/companyApiSlice";
import { Driver } from "../../../../features/driver/@types";
import { useGetDriversMutation } from "../../../../features/driver/driverApiSlice";
import { Partner } from "../../../../features/partner/@types";
import { useGetPartnersMutation } from "../../../../features/partner/partnerApiSlice";
import { Staff } from "../../../../features/staff/@types";
import { useGetAllStaffMutation } from "../../../../features/staff/staffApiSlice";
import { User } from "../../../../features/user/@types";
import { useGetUsersMutation } from "../../../../features/user/userApiSlice";
import { Vehicle } from "../../../../features/vehicle/@types";
import { useGetVehiclesMutation } from "../../../../features/vehicle/vehicleApiSlice";
import {
  ADMIN_PAGE_LINKS,
  extractSearchItems,
  HeaderSearchResult,
  matchesPageLink,
  SUPER_ADMIN_PAGE_LINKS,
} from "./headerUtils";

interface HeaderSearchProps {
  isOpen: boolean;
  onOpen: () => void;
  onClose: () => void;
  inputRef: React.RefObject<HTMLInputElement | null>;
}

const SEARCH_PAGE_SIZE = 4;

const HeaderSearch: React.FC<HeaderSearchProps> = ({
  isOpen,
  onOpen,
  onClose,
  inputRef,
}) => {
  const navigate = useNavigate();
  const { canView } = usePermissions();
  const location = useLocation();
  const isSuperAdmin = location.pathname.startsWith("/super-admin");

  const [query, setQuery] = useState("");
  const [highlightedIndex, setHighlightedIndex] = useState(0);
  const [isSearching, setIsSearching] = useState(false);
  const [entityResults, setEntityResults] = useState<HeaderSearchResult[]>([]);
  const debouncedQuery = useDebouncedValue(query.trim(), 350);

  const [getVehicles] = useGetVehiclesMutation();
  const [getDrivers] = useGetDriversMutation();
  const [getPartners] = useGetPartnersMutation();
  const [getStaff] = useGetAllStaffMutation();
  const [getUsers] = useGetUsersMutation();
  const [getCompanies] = useGetCompaniesMutation();

  const pageLinks = useMemo(
    () =>
      isSuperAdmin
        ? SUPER_ADMIN_PAGE_LINKS
        : ADMIN_PAGE_LINKS.filter(
            (page) => !page.permission || canView(page.permission)
          ),
    [isSuperAdmin, canView]
  );

  const pageResults = useMemo<HeaderSearchResult[]>(() => {
    const filtered = pageLinks.filter((page) => matchesPageLink(page, query));
    const limited = query.trim() ? filtered : filtered.slice(0, 8);
    return limited.map((page) => ({
      id: `page-${page.id}`,
      category: "Pages",
      title: page.label,
      path: page.path,
    }));
  }, [pageLinks, query]);

  useEffect(() => {
    let cancelled = false;

    const run = async () => {
      if (debouncedQuery.length < 2) {
        setEntityResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      const request = {
        pageNumber: 0,
        pageSize: SEARCH_PAGE_SIZE,
        keyword: debouncedQuery,
      };

      try {
        const results: HeaderSearchResult[] = [];

        if (isSuperAdmin) {
          const [usersRes, companiesRes] = await Promise.allSettled([
            getUsers(request).unwrap(),
            getCompanies(request).unwrap(),
          ]);

          if (usersRes.status === "fulfilled") {
            extractSearchItems<User>(usersRes.value).forEach((user) => {
              results.push({
                id: `user-${user.id}`,
                category: "Users",
                title: user.fullName,
                subtitle: user.email,
                path: `/super-admin/user/${user.id}`,
              });
            });
          }

          if (companiesRes.status === "fulfilled") {
            extractSearchItems<Company>(companiesRes.value).forEach(
              (company) => {
                results.push({
                  id: `company-${company.id}`,
                  category: "Companies",
                  title: company.fullName,
                  subtitle: company.email,
                  path: "/super-admin/companies",
                });
              }
            );
          }
        } else {
          const denied = () => Promise.reject(new Error("No permission"));
          const [vehiclesRes, driversRes, partnersRes, staffRes] =
            await Promise.allSettled([
              canView("Vehicles") ? getVehicles(request).unwrap() : denied(),
              canView("Company") ? getDrivers(request).unwrap() : denied(),
              canView("Company") ? getPartners(request).unwrap() : denied(),
              canView("Users") ? getStaff(request).unwrap() : denied(),
            ]);

          if (vehiclesRes.status === "fulfilled") {
            extractSearchItems<Vehicle>(vehiclesRes.value).forEach((vehicle) => {
              results.push({
                id: `vehicle-${vehicle.id}`,
                category: "Vehicles",
                title: vehicle.vehicleName || vehicle.licensePlate,
                subtitle: [vehicle.licensePlate, vehicle.vehicleModel]
                  .filter(Boolean)
                  .join(" · "),
                path: `/admin/vehicles/${vehicle.id}`,
              });
            });
          }

          if (driversRes.status === "fulfilled") {
            extractSearchItems<Driver>(driversRes.value).forEach((driver) => {
              results.push({
                id: `driver-${driver.id}`,
                category: "Drivers",
                title: driver.fullName,
                subtitle: driver.email || driver.currentVehicle,
                path: `/admin/drivers/${driver.id}`,
              });
            });
          }

          if (partnersRes.status === "fulfilled") {
            extractSearchItems<Partner>(partnersRes.value).forEach((partner) => {
              results.push({
                id: `partner-${partner.id}`,
                category: "Partners",
                title: partner.fullName,
                subtitle: partner.email,
                path: `/admin/partners/${partner.id}`,
              });
            });
          }

          if (staffRes.status === "fulfilled") {
            extractSearchItems<Staff>(staffRes.value).forEach((staff) => {
              results.push({
                id: `staff-${staff.id}`,
                category: "Users",
                title: staff.fullName,
                subtitle: staff.email || staff.role,
                path: `/admin/user/${staff.id}`,
              });
            });
          }
        }

        if (!cancelled) setEntityResults(results);
      } finally {
        if (!cancelled) setIsSearching(false);
      }
    };

    void run();
    return () => {
      cancelled = true;
    };
  }, [
    canView,
    debouncedQuery,
    getCompanies,
    getDrivers,
    getPartners,
    getStaff,
    getUsers,
    getVehicles,
    isSuperAdmin,
  ]);

  const results = useMemo(
    () => [...pageResults, ...entityResults],
    [entityResults, pageResults]
  );

  useEffect(() => {
    setHighlightedIndex(0);
  }, [results.length, query]);

  const goToResult = (result: HeaderSearchResult) => {
    navigate(result.path);
    setQuery("");
    setEntityResults([]);
    onClose();
  };

  const handleKeyDown = (event: React.KeyboardEvent<HTMLInputElement>) => {
    if (!isOpen && event.key !== "Escape") onOpen();

    if (event.key === "ArrowDown") {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        results.length === 0 ? 0 : (prev + 1) % results.length
      );
      return;
    }

    if (event.key === "ArrowUp") {
      event.preventDefault();
      setHighlightedIndex((prev) =>
        results.length === 0 ? 0 : (prev - 1 + results.length) % results.length
      );
      return;
    }

    if (event.key === "Enter") {
      event.preventDefault();
      const selected = results[highlightedIndex] ?? results[0];
      if (selected) goToResult(selected);
      return;
    }

    if (event.key === "Escape") {
      onClose();
      inputRef.current?.blur();
    }
  };

  const groupedResults = useMemo(() => {
    const groups = new Map<string, HeaderSearchResult[]>();
    results.forEach((result) => {
      const items = groups.get(result.category) ?? [];
      items.push(result);
      groups.set(result.category, items);
    });
    return Array.from(groups.entries());
  }, [results]);

  let runningIndex = -1;
  const showEmpty =
    isOpen &&
    !isSearching &&
    query.trim().length > 0 &&
    results.length === 0;

  return (
    <div className="relative hidden md:block md:w-[280px] lg:w-[420px]">
      <Search
        className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none"
        size={18}
      />
      <input
        ref={inputRef}
        type="text"
        role="combobox"
        aria-expanded={isOpen}
        aria-controls="header-search-results"
        aria-autocomplete="list"
        value={query}
        onChange={(event) => {
          setQuery(event.target.value);
          onOpen();
        }}
        onFocus={onOpen}
        onKeyDown={handleKeyDown}
        placeholder="Search anything here"
        className="h-10 w-full rounded-xl border border-slate-200 bg-slate-50 py-2 pl-10 pr-16 text-base text-slate-900 placeholder:text-slate-400 transition-all duration-200 hover:bg-white focus:border-primary focus:bg-white focus:outline-none focus:ring-4 focus:ring-primary/10 md:text-sm"
      />
      <div className="absolute right-3 top-1/2 -translate-y-1/2 flex items-center gap-1">
        {isSearching && (
          <Loader2 className="w-4 h-4 text-primary animate-spin" />
        )}
        {query ? (
          <button
            type="button"
            onClick={() => {
              setQuery("");
              setEntityResults([]);
              inputRef.current?.focus();
            }}
            className="p-1 rounded-full text-gray-400 hover:text-hColor hover:bg-white"
            aria-label="Clear search"
          >
            <X size={14} />
          </button>
        ) : (
          <kbd className="hidden md:inline-flex items-center rounded-md border border-gray-200 bg-white px-1.5 py-0.5 text-[10px] font-medium text-pColor">
            ⌘K
          </kbd>
        )}
      </div>

      <AnimatePresence>
        {isOpen && (
          <motion.div
            id="header-search-results"
            role="listbox"
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.15 }}
            className="absolute left-0 right-0 top-[calc(100%+8px)] z-50 max-h-[420px] overflow-y-auto rounded-2xl border border-gray-100 bg-white shadow-xl"
          >
            {showEmpty ? (
              <p className="px-4 py-6 text-sm text-center text-pColor">
                No results for “{query.trim()}”
              </p>
            ) : (
              groupedResults.map(([category, items]) => (
                <div key={category} className="py-2">
                  <p className="px-4 py-1 text-[11px] font-semibold uppercase tracking-wide text-pColor">
                    {category}
                  </p>
                  {items.map((item) => {
                    runningIndex += 1;
                    const index = runningIndex;
                    const isActive = index === highlightedIndex;
                    return (
                      <button
                        key={item.id}
                        type="button"
                        role="option"
                        aria-selected={isActive}
                        onMouseEnter={() => setHighlightedIndex(index)}
                        onClick={() => goToResult(item)}
                        className={`w-full px-4 py-2.5 text-left transition-colors ${
                          isActive ? "bg-[#EDF2DFB2]" : "hover:bg-gray-50"
                        }`}
                      >
                        <p className="text-sm font-medium text-hColor truncate">
                          {item.title}
                        </p>
                        {item.subtitle && (
                          <p className="text-xs text-pColor truncate">
                            {item.subtitle}
                          </p>
                        )}
                      </button>
                    );
                  })}
                </div>
              ))
            )}
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default HeaderSearch;
